// Fase 2 (G3 a, 07-10-2026): de tien velden die een werkgever over een
// regeling vraagt, en het dekkingslabel "x van 14 velden".
//
// EIGEN COMPONENT, zodat FundingDetail.tsx (gedeeld met andere banen) er
// maar twee regels voor hoeft te dragen: de import en één <FundingAanvulling>.
// Dezelfde regels als het detail:
//  · elk veld met een waarde toont zijn letterlijke citaat van de officiële
//    pagina eronder (bewijs);
//  · een leeg veld toont zijn REDEN (vlag uit de leespas), nooit een leeg vak;
//  · de herkomstregel onderaan het detail geldt ook voor deze velden.

import type { Funding } from "../contracts";
import { fmtDatum } from "../lib/venster";
import { color, radius, size, weight } from "../tokens";
import { EmptyReason } from "./Empty";
import { SectionLabel } from "./ui";

/** Waarom een nieuw veld leeg is, in gewone taal. */
export const AANVULLING_VLAG: Record<string, string> = {
  vergoedingspercentage_niet_vermeld: "De pagina noemt geen percentage van de kosten.",
  vergoedingspercentage_niet_in_bron:
    "Het voorgestelde percentage stond niet als getal op de pagina en is niet overgenomen.",
  plafond_niet_vermeld: "De pagina noemt geen subsidieplafond.",
  plafond_niet_in_bron:
    "Het voorgestelde plafond stond niet als getal op de pagina en is niet overgenomen.",
  per_bedrijf_of_persoon_niet_vermeld: "De pagina zegt niet of de bijdrage per bedrijf of per persoon geldt.",
  mkb_eis_niet_vermeld: "De pagina zegt niet of de regeling alleen voor het mkb is.",
  regio_niet_vermeld: "De pagina noemt geen gebied.",
  combineerbaarheid_niet_vermeld: "De pagina zegt niets over combineren met andere regelingen.",
  looptijd_niet_vermeld: "De pagina noemt geen looptijd van de regeling zelf.",
  aanvraagportaal_niet_vermeld: "De pagina noemt geen link naar een aanvraagportaal.",
  aanvraagportaal_niet_in_bron: "De voorgestelde link stond niet letterlijk op de pagina.",
  regelingtekst_niet_vermeld: "De pagina linkt niet naar de officiële regelingtekst.",
  regelingtekst_url_niet_in_bron: "De voorgestelde link stond niet letterlijk op de pagina.",
  laatste_wijziging_niet_vermeld: "De pagina noemt geen datum van laatste wijziging.",
  // De pagina noemde wel iets, maar het viel niet te verifiëren (reviewronde
  // fase 2: deze vlaggen vielen eerder terug op "noemt geen …").
  "looptijddatum_niet_in_bron:start":
    "De pagina noemt een looptijd, maar de begindatum stond niet letterlijk op de pagina.",
  "looptijddatum_niet_in_bron:eind":
    "De pagina noemt een looptijd, maar de einddatum stond niet letterlijk op de pagina.",
  looptijd_zonder_datum: "De pagina noemt een looptijd zonder datum die we kunnen overnemen.",
  "laatste_wijziging_datum_niet_in_bron:start":
    "De pagina noemt een wijziging, maar de datum stond niet letterlijk op de pagina.",
  laatste_wijziging_zonder_datum: "De pagina noemt een wijziging zonder datum die we kunnen overnemen.",
  plafond_eenheid_onduidelijk: "De pagina noemt een plafond, maar niet waarover het geldt.",
  herlezing_ruis:
    "De laatste lezing vond iets, maar het citaat stond niet op de pagina. We houden het " +
    "pas aan als het bevestigd is.",
  nakijken: "De laatste lezing wijkt af; dit veld wacht op nakijken.",
  citaat_van_andere_pagina:
    "Er stond wel iets, maar op een pagina die niet over deze regeling gaat " +
    "(een overzicht of een andere regeling). Dat telt niet als bewijs.",
  zonder_citaat: "Er stond wel iets, maar niet letterlijk citeerbaar. Zonder bewijs laten we het weg.",
  pagina_bevat_geen_regeling:
    "De officiële pagina in ons register gaat niet over deze regeling zelf.",
  nog_niet_gelezen: "Deze regeling is nog niet gelezen met de nieuwe leesvraag.",
  gelezen_met_oudere_leesversie:
    "Deze vraag is de pagina nog niet gesteld: de laatste lezing is van een oudere leesversie.",
};

/** De kolommen van een veld (de rem vlagt op kolomnaam: herlezing_ruis:<kolom>). */
const KOLOMMEN_VAN: Record<string, readonly string[]> = {
  plafond: ["plafond_bedrag", "plafond_eenheid"],
  bedrag: ["max_bedrag", "bedrag_eenheid"],
  looptijd_regeling: ["looptijd_start", "looptijd_eind"],
};

/** Vlaggen van een veld die niet met `<veld>_` beginnen. */
const EXTRA_VLAGGEN: Record<string, readonly string[]> = {
  looptijd_regeling: [
    "looptijddatum_niet_in_bron:start",
    "looptijddatum_niet_in_bron:eind",
    "looptijd_zonder_datum",
  ],
};

/** De reden voor een leeg veld. Volgorde (reviewronde fase 2): eerst wat voor
 *  de hele regeling geldt (de pagina gaat er niet over, de vraag is nog niet
 *  gesteld), dan de eigen vlag van het veld, dan de terugval. Zo zegt het
 *  scherm nooit "de pagina noemt geen …" over een vraag die niet gesteld is. */
export function redenVoor(vlaggen: readonly string[], veld: string, terugval: string): string {
  return bijzondereReden(vlaggen, veld) ?? AANVULLING_VLAG[terugval] ?? STANDAARD_REDEN;
}

const STANDAARD_REDEN = "De officiële pagina zegt het niet.";
/** De vlaggen die voor de hele regeling gelden: dan zegt geen enkel leeg veld
 *  iets over wat de pagina noemt (review fase 6). */
const ALGEMENE_VLAGGEN = [
  "pagina_bevat_geen_regeling",
  "nog_niet_gelezen",
  "gelezen_met_oudere_leesversie",
] as const;

/** De reden uit de vlaggen, zonder terugval: null = geen bijzondere reden
 *  (de pagina is gelezen en zweeg). */
export function bijzondereReden(vlaggen: readonly string[], veld: string): string | null {
  const uitleg = (sleutel: string) => AANVULLING_VLAG[sleutel] ?? STANDAARD_REDEN;
  for (const algemeen of ALGEMENE_VLAGGEN) {
    if (vlaggen.includes(algemeen)) return uitleg(algemeen);
  }
  if (vlaggen.includes(`citaat_van_andere_pagina:${veld}`)) return uitleg("citaat_van_andere_pagina");
  const kolommen = KOLOMMEN_VAN[veld] ?? [veld];
  if (kolommen.some((k) => vlaggen.includes(`herlezing_ruis:${k}`))) return uitleg("herlezing_ruis");
  if (kolommen.some((k) => vlaggen.includes(`nakijken:${k}`))) return uitleg("nakijken");
  if (vlaggen.includes(`${veld}_zonder_citaat`)) return uitleg("zonder_citaat");
  const extra = (EXTRA_VLAGGEN[veld] ?? []).find((v) => vlaggen.includes(v));
  if (extra) return uitleg(extra);
  const eigen = vlaggen.find((v) => v.startsWith(`${veld}_`) && v in AANVULLING_VLAG);
  if (eigen) return uitleg(eigen);
  return null;
}

/** Review fase 6: de tekst voor een leeg klassiek veld (bedrag, doelgroep-eis,
 *  hoe aanvragen) buiten het detail. `zegtNiet` ("de officiële pagina noemt
 *  er geen") alleen als de lezing het veld echt leeg vond (`terugvalVlag`);
 *  anders de reden uit de vlaggen, of dat er geen lezing over is. */
export function legeVeldTekst(
  vlaggen: readonly string[] | undefined,
  veld: string,
  terugvalVlag: string,
  zegtNiet: string,
): string {
  const v = vlaggen ?? [];
  const reden = bijzondereReden(v, veld);
  if (reden) return reden;
  if (v.includes(terugvalVlag)) return zegtNiet;
  return "Hierover is geen lezing van de officiële pagina vastgelegd.";
}

/** Het label "x van 14 velden"; niets als de server geen dekking stuurt. */
export function DekkingLabel({
  dekking,
  los = false,
  vlaggen,
}: {
  dekking: Funding["dekking"];
  /** los = als eigen regel (detail) in plaats van " · …" achter een regel (lijst). */
  los?: boolean;
  /** Review fase 6: met de vlaggen zegt de tooltip niet "niet gevonden" over
   *  een regeling die (met deze leesvraag) nog niet gelezen is. */
  vlaggen?: readonly string[];
}) {
  if (!dekking) return null;
  const tekst = `${dekking.gevuld} van ${dekking.van} velden`;
  const leeg = dekking.leeg.join(", ").replace(/_/g, " ");
  const algemeen = ALGEMENE_VLAGGEN.find((a) => (vlaggen ?? []).includes(a));
  const titel =
    dekking.leeg.length === 0
      ? "Alle velden gevonden op de officiële pagina('s)"
      : algemeen
        ? `Leeg: ${leeg}. ${AANVULLING_VLAG[algemeen]}`
        : `Op de officiële pagina('s) niet gevonden: ${leeg}`;
  return (
    <span
      data-testid="dekking"
      title={titel}
      style={{ fontSize: size.label, color: color.faint, whiteSpace: "nowrap" }}
    >
      {los ? tekst : ` · ${tekst}`}
    </span>
  );
}

function Citaat({ citaat }: { citaat: string }) {
  return (
    <blockquote
      style={{
        margin: "4px 0 0",
        padding: "6px 10px",
        background: color.surfaceQuote,
        borderLeft: `2px solid ${color.border}`,
        borderRadius: radius.quote,
        fontSize: size.label,
        color: color.muted,
        lineHeight: 1.55,
      }}
    >
      “{citaat}”
    </blockquote>
  );
}

function Regel({
  label,
  waarde,
  reden,
  citaat,
  link,
}: {
  label: string;
  waarde: string | null;
  reden: string;
  citaat?: string;
  link?: string | null;
}) {
  return (
    <div style={{ padding: "8px 0", borderBottom: `1px solid ${color.lineFaint}` }}>
      <div style={{ display: "flex", gap: 12, alignItems: "baseline", flexWrap: "wrap" }}>
        <span style={{ fontSize: size.label, color: color.faint, minWidth: 150 }}>{label}</span>
        {waarde ? (
          link ? (
            <a
              href={link}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                fontSize: size.meta,
                color: color.body,
                fontWeight: weight.medium,
                overflowWrap: "anywhere",
              }}
            >
              {waarde} ↗
            </a>
          ) : (
            <span style={{ fontSize: size.meta, color: color.body, fontWeight: weight.medium }}>
              {waarde}
            </span>
          )
        ) : (
          <EmptyReason reason={reden} />
        )}
      </div>
      {waarde && citaat && <Citaat citaat={citaat} />}
    </div>
  );
}

function euro(bedrag: number): string {
  return `€ ${bedrag.toLocaleString("nl-NL")}`;
}

function host(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}

/** De tien velden van fase 2 als één blok "Voorwaarden en details". */
export function FundingAanvulling({ funding }: { funding: Funding }) {
  // Een antwoord van vóór fase 2 kent deze velden niet: dan geen blok (het
  // contract maakt ze optioneel), in plaats van tien keer "onbekend".
  if (funding.plafond === undefined && funding.dekking === undefined) return null;
  const vlaggen = funding.vlaggen ?? [];
  const bewijs = (veld: string) => funding.bewijs?.find((b) => b.veld === veld)?.citaat;
  const looptijd = funding.looptijdRegeling;
  const looptijdTekst =
    looptijd && (looptijd.start || looptijd.eind)
      ? looptijd.start && looptijd.eind
        ? `${fmtDatum(looptijd.start)} – ${fmtDatum(looptijd.eind)}`
        : looptijd.eind
          ? `tot ${fmtDatum(looptijd.eind)}`
          : `vanaf ${fmtDatum(looptijd.start)}`
      : null;
  const plafond = funding.plafond;
  const keuze = (k?: { waarde: string; label: string }) =>
    k && k.waarde !== "onbekend" ? k.label : null;

  return (
    <div data-testid="funding-aanvulling" style={{ marginBottom: 22 }}>
      <SectionLabel>Voorwaarden en details</SectionLabel>
      <Regel
        label="Vergoedt"
        waarde={
          funding.vergoedingspercentage != null
            ? `${funding.vergoedingspercentage.toLocaleString("nl-NL")}% van de kosten`
            : null
        }
        reden={redenVoor(vlaggen, "vergoedingspercentage", "vergoedingspercentage_niet_vermeld")}
        citaat={bewijs("vergoedingspercentage")}
      />
      <Regel
        label="Geldt per"
        waarde={keuze(funding.perBedrijfOfPersoon)}
        reden={redenVoor(vlaggen, "per_bedrijf_of_persoon", "per_bedrijf_of_persoon_niet_vermeld")}
        citaat={bewijs("per_bedrijf_of_persoon")}
      />
      <Regel
        label="Alleen mkb?"
        waarde={keuze(funding.mkbEis)}
        reden={redenVoor(vlaggen, "mkb_eis", "mkb_eis_niet_vermeld")}
        citaat={bewijs("mkb_eis")}
      />
      <Regel
        label="Gebied"
        waarde={funding.regio ?? null}
        reden={redenVoor(vlaggen, "regio", "regio_niet_vermeld")}
        citaat={bewijs("regio")}
      />
      <Regel
        label="Combineren"
        waarde={funding.combineerbaarheid ?? null}
        reden={redenVoor(vlaggen, "combineerbaarheid", "combineerbaarheid_niet_vermeld")}
        citaat={bewijs("combineerbaarheid")}
      />
      <Regel
        label="Subsidieplafond"
        waarde={
          plafond && plafond.bedrag != null
            ? `${euro(plafond.bedrag)}${plafond.waarde !== "onbekend" ? ` · ${plafond.label.toLowerCase()}` : ""}`
            : null
        }
        reden={redenVoor(vlaggen, "plafond", "plafond_niet_vermeld")}
        citaat={bewijs("plafond")}
      />
      <Regel
        label="Looptijd regeling"
        waarde={looptijdTekst}
        reden={redenVoor(vlaggen, "looptijd_regeling", "looptijd_niet_vermeld")}
        citaat={bewijs("looptijd_regeling")}
      />
      <Regel
        label="Aanvragen via"
        waarde={funding.aanvraagportaal ? host(funding.aanvraagportaal) : null}
        link={funding.aanvraagportaal}
        reden={redenVoor(vlaggen, "aanvraagportaal", "aanvraagportaal_niet_vermeld")}
      />
      <Regel
        label="Regelingtekst"
        waarde={funding.regelingtekstUrl ? host(funding.regelingtekstUrl) : null}
        link={funding.regelingtekstUrl}
        reden={redenVoor(vlaggen, "regelingtekst_url", "regelingtekst_niet_vermeld")}
      />
      <Regel
        label="Laatst gewijzigd"
        waarde={funding.laatsteWijziging ? `${fmtDatum(funding.laatsteWijziging)} (volgens de pagina)` : null}
        reden={redenVoor(vlaggen, "laatste_wijziging", "laatste_wijziging_niet_vermeld")}
        citaat={bewijs("laatste_wijziging")}
      />
    </div>
  );
}
